'use client';

import { useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { SectionHeader, Grad } from '@/components/ui/Section';
import { ArrowLink } from '@/components/ui/Button';

/**
 * "Applications Across Industries" — dark tabbed showcase.
 *
 * One application panel is visible at a time, selected from a segmented tab
 * bar. All four images stay mounted and cross-fade (opacity only), so tab
 * switches never wait on an image request and never shift layout. Text content
 * is remounted per tab with the site's standard `.m-fade`/`.rise` entrance.
 *
 * All copy and figures below are the section's existing content — nothing new
 * was invented.
 */

type Application = {
  id: string;
  tab: string;
  icon: string;
  eyebrow: string;
  title: string;
  description: string;
  metric: string;
  metricText: string;
  secondaryLabel: string;
  secondaryValue?: string;
  image: string;
  imageAlt: string;
};

const applications: Application[] = [
  {
    id: 'lubricants',
    tab: 'Lubricants',
    icon: 'BeakerIcon',
    eyebrow: 'Lubricants',
    title: 'Automotive Lubricants',
    description: 'Reduce friction and extend engine life with graphene-enhanced lubricants',
    metric: '40%',
    metricText: 'improved wear resistance',
    secondaryLabel: 'Friction coefficient:',
    secondaryValue: 'Decreases by several folds',
    image: '/assets/images/Automotive-Lubricants.png',
    imageAlt:
      'Automotive engine components with lubricant oil coating showing reduced friction surfaces',
  },
  {
    id: 'concrete',
    tab: 'Concrete',
    icon: 'CubeIcon',
    eyebrow: 'Concrete',
    title: 'High-Performance Concrete',
    description: 'Enhance durability and reduce cracking with nanomaterial reinforcement',
    metric: '35%',
    metricText: 'increased compressive strength',
    secondaryLabel: 'Proven Data Available',
    image: '/assets/images/home-concrete.jpg',
    imageAlt: 'Coastal bridge construction showing reinforced high-performance concrete structures',
  },
  {
    id: 'composites',
    tab: 'Composites',
    icon: 'Square3Stack3DIcon',
    eyebrow: 'Composites',
    title: 'Composite Applications',
    description: 'Lightweight, high-strength materials for demanding applications',
    metric: '40%',
    metricText: 'improved thermal conductivity',
    secondaryLabel: 'Weight reduction:',
    secondaryValue: '15–20%',
    image: '/assets/images/home-composites.png',
    imageAlt: 'Layered carbon-fibre composite material with graphene reinforcement',
  },
  {
    id: 'rubber',
    tab: 'Rubber',
    icon: 'LifebuoyIcon',
    eyebrow: 'Rubber',
    title: 'Industrial Rubber',
    description: 'Improved mechanical and thermal properties',
    metric: '60%',
    metricText: 'enhanced electrical conductivity',
    secondaryLabel: 'Tensile modulus:',
    secondaryValue: '+45%',
    // The section's original rubber visual — no local gasket/seal image exists.
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10c6bda9a-1770116878378.png',
    imageAlt: 'Industrial rubber seals and gaskets with enhanced conductivity properties',
  },
];

/** Panel surface colour — shared by the card and the image blend overlays. */
const PANEL = '#0a1322';

const IndustryApplications = () => {
  const [activeId, setActiveId] = useState(applications[0].id);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const active = applications.find((a) => a.id === activeId) ?? applications[0];

  /** Left/Right arrows move selection between tabs (roving focus). */
  const onTablistKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = applications.findIndex((a) => a.id === activeId);
    const next =
      e.key === 'ArrowRight'
        ? (i + 1) % applications.length
        : (i - 1 + applications.length) % applications.length;
    setActiveId(applications[next].id);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="applications" className="section relative overflow-hidden bg-ink text-white">
      {/* One restrained blue cast so the band doesn't read as flat black. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(65rem_30rem_at_50%_-12%,rgba(14,165,233,0.1),transparent_60%)]"
      />

      <div className="container-page relative">
        {/* Centered heading — standardised presentation. */}
        <SectionHeader
          title={
            <>
              Applications Across <Grad>Industries</Grad>
            </>
          }
          eyebrow="Applications"
          lead="Proven performance enhancements in lubricants, concrete, composites, and rubber applications"
        />

        {/* Tab bar */}
        <div
          role="tablist"
          aria-label="Application industries"
          onKeyDown={onTablistKeyDown}
          className="mx-auto mt-10 grid max-w-3xl grid-cols-2 gap-1 rounded-xl border border-white/10 bg-white/[0.04] p-1.5 sm:flex sm:justify-center"
        >
          {applications.map((app, i) => {
            const isActive = app.id === activeId;
            return (
              <button
                key={app.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                role="tab"
                id={`app-tab-${app.id}`}
                aria-selected={isActive}
                aria-controls={`app-panel-${app.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveId(app.id)}
                className={`flex min-h-[46px] flex-1 items-center justify-center gap-2.5 rounded-lg px-4 text-[0.8125rem] font-semibold uppercase tracking-[0.08em] transition-[background-color,color,box-shadow] duration-base ease-out ${
                  isActive
                    ? 'bg-accent-rich text-white shadow-[0_6px_20px_rgba(14,165,233,0.35)]'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon name={app.icon} size={18} className="shrink-0" />
                <span className="truncate">{app.tab}</span>
              </button>
            );
          })}
        </div>

        {/* Featured panel — one application at a time. */}
        <div
          role="tabpanel"
          id={`app-panel-${active.id}`}
          aria-labelledby={`app-tab-${active.id}`}
          className="mt-8 overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(2,8,23,0.8),0_0_50px_-12px_rgba(14,165,233,0.18)]"
          style={{ backgroundColor: PANEL }}
        >
          <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            {/* Text + metrics. Keyed so the entrance replays on each switch. */}
            <div key={active.id} className="m-fade order-2 min-w-0 p-7 sm:p-10 lg:order-1 lg:p-12">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                {active.eyebrow}
              </p>

              <h3 className="t-h2 mt-3 text-white">{active.title}</h3>
              <div aria-hidden="true" className="mt-4 h-0.5 w-12 rounded-full bg-accent" />

              <p className="t-body measure mt-5 text-slate-300">{active.description}</p>

              <p className="mt-8">
                <span className="t-value text-accent-grad block text-[4.25rem] font-bold leading-[0.95] tracking-tight sm:text-[5.25rem]">
                  {active.metric}
                </span>
                <span className="mt-2 block text-lg font-semibold text-white sm:text-xl">
                  {active.metricText}
                </span>
              </p>

              <div className="mt-8 flex items-center gap-4 border-t border-white/10 pt-7">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10">
                  <Icon name="ShieldCheckIcon" size={20} className="text-accent" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[0.9375rem] font-medium text-white">
                    {active.secondaryLabel}
                  </span>
                  {active.secondaryValue && (
                    <span className="block text-sm text-slate-400">{active.secondaryValue}</span>
                  )}
                </span>
              </div>
            </div>

            {/* Image — all four stay mounted and cross-fade. */}
            <div className="relative order-1 aspect-[16/10] min-w-0 lg:order-2 lg:aspect-auto lg:min-h-[520px]">
              {applications.map((app) => (
                <div
                  key={app.id}
                  aria-hidden={app.id !== active.id}
                  className={`absolute inset-0 transition-opacity duration-slow ease-out ${
                    app.id === active.id ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <AppImage
                    src={app.image}
                    alt={app.imageAlt}
                    fill
                    sizes="(max-width: 1024px) 100vw, 55vw"
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}

              {/* Blend the image into the panel: left edge on desktop, top edge
                  on mobile (where the image sits above the text). */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 hidden w-2/5 lg:block"
                style={{ background: `linear-gradient(90deg, ${PANEL}, transparent)` }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-20 lg:hidden"
                style={{ background: `linear-gradient(0deg, ${PANEL}, transparent)` }}
              />
            </div>
          </div>
        </div>

        <div className="mt-9 text-center">
          <ArrowLink href="/services" tone="dark">
            See all applications and technical data
          </ArrowLink>
        </div>
      </div>
    </section>
  );
};

export default IndustryApplications;
